import { TrashIcon } from "@heroicons/react/24/solid";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base, Label } from "@/atoms-react/text/Text.react";
import type { FullCountry, NaturalPerson, NaturalPersonDTO } from "@/features/user/types";
import { formStyle } from "../level-card-status-container/form-style";

export type TaxAddressSchema = {
  uuid?: string;
  countryId: string | null;
  tinExist: boolean;
  tin: string | null;
  tinCheck: boolean | null;
  apiCheck: boolean;
};

type UserIdentityProps = {
  taxAddresses: {
    taxAddresses: TaxAddressSchema[];
    isDirty: boolean;
  };
  setTaxAddresses: Dispatch<
    SetStateAction<{
      taxAddresses: TaxAddressSchema[];
      isDirty: boolean;
    }>
  >;
  countriesData: { countries: FullCountry[] } | undefined;
  error: string[];
  isRequestPending: boolean;
  refetchUser?: () => void;
  onInputChange: (userForm: NaturalPersonDTO & { isDirty: boolean }) => void;
  naturalPerson?: NaturalPerson;
  disabled: boolean;
  userForm: NaturalPersonDTO & { isDirty: boolean };
} & React.ComponentPropsWithoutRef<"div">;

export const FormTaxAdresseIdentity = ({
  taxAddresses,
  setTaxAddresses,
  countriesData,
  error,
  isRequestPending,
  disabled
}: UserIdentityProps) => {
  const { t } = useTranslation(["pass"]);

  // States //
  const [askToAdd, setAskToAdd] = useState<boolean>(false);

  // It is made to add a taxaddress and switch back checkbox to "No" state (design)
  useEffect(() => {
    if (!askToAdd) return;

    addTaxAddress();
    setAskToAdd(false);
  }, [askToAdd]);

  // Events //
  const addTaxAddress = () => {
    if (taxAddresses.taxAddresses.length >= 4) return;

    const newTaxAdress: TaxAddressSchema = {
      uuid: undefined,
      apiCheck: false,
      countryId: null,
      tin: null,
      tinCheck: null,
      tinExist: false
    };
    setTaxAddresses((prev) => ({
      taxAddresses: [...prev.taxAddresses, newTaxAdress],
      isDirty: true
    }));
  };

  const deleteTaxAddress = (index: number) => {
    if (index === 0 || taxAddresses.taxAddresses[index].apiCheck) return;
    const newTaxAddresses = Array.from(taxAddresses.taxAddresses);
    setTaxAddresses({
      taxAddresses: newTaxAddresses.filter((_, indexFilter) => indexFilter !== index),
      isDirty: true
    });
  };

  const onCountryChange = (index: number, countryId: string) => {
    if (taxAddresses.taxAddresses.find((taxAddress) => taxAddress.countryId === countryId)) {
      toast.error(t("flow.id_control.input.nationality_add_error"));
    }

    setTaxAddresses((prev) => ({
      taxAddresses: prev.taxAddresses.map((taxAddress, mapIndex) => {
        if (mapIndex === index) return { ...taxAddress, countryId: countryId };
        else return taxAddress;
      }),
      isDirty: true
    }));
  };

  const onNifChange = (index: number, value: string) => {
    setTaxAddresses((prev) => ({
      taxAddresses: prev.taxAddresses.map((taxAddress, mapIndex) => {
        if (mapIndex === index) return { ...taxAddress, tin: value };
        else return taxAddress;
      }),
      isDirty: true
    }));
  };

  return (
    <div className={twMerge(formStyle.containerLine, "items-start")}>
      <div className={twMerge(formStyle.containerLineTitle, "mt-1")}>
        <Label>{t("olkypass.forms.declarative.nif.title")}</Label>
      </div>
      <div className="flex grow flex-col">
        {taxAddresses.taxAddresses.map((taxAddressItem, taxAddressIndex) => (
          <div
            key={taxAddressIndex}
            className="mb-3 flex flex-row flex-wrap items-center truncate sm:flex-nowrap"
          >
            {!countriesData ? (
              <Loader className="w-full truncate sm:w-48 sm:max-w-48" />
            ) : (
              <Dropdown
                className="w-40 min-w-40 max-w-40"
                value={taxAddressItem.countryId}
                disabled={taxAddressItem.apiCheck || isRequestPending || disabled}
                items={countriesData.countries.map((country) => ({
                  key: country.id.toString(),
                  value: country.defaultName,
                  icon: <Flag countryCode={country.alpha2.toLowerCase()} />
                }))}
                onChange={(item) => onCountryChange(taxAddressIndex, item.key)}
                search={true}
              />
            )}
            <div className="h-4 w-4" />
            <InputLabel
              autoComplete="given-name"
              placeholder={t("olkypass.accountPage.basicInfo.input.nif")}
              error={
                error.includes(taxAddressItem.countryId ?? "")
                  ? t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error")
                  : undefined
              }
              value={taxAddressItem.tin ?? ""}
              disabled={taxAddressItem.apiCheck || isRequestPending || disabled}
              onChange={(value) => onNifChange(taxAddressIndex, value)}
              suffixIcon={
                taxAddressIndex !== 0 &&
                !taxAddressItem.apiCheck &&
                !taxAddressItem.uuid && <TrashIcon className="text-red-600" />
              }
              onClickSuffixIcon={() => deleteTaxAddress(taxAddressIndex)}
            />
          </div>
        ))}

        {/* Add Tax Address Button */}
        {taxAddresses.taxAddresses.length < 4 && (
          <div className="mt-2 mb-2">
            <Base className="mt-4 text-base">
              {t("olkypass.forms.declarative.nif.secondNifQuestion")}
            </Base>
            <div className="mt-4 flex w-full flex-row">
              <Checkbox
                value={askToAdd}
                disabled={isRequestPending}
                onChange={() => setAskToAdd(true)}
              >
                <Base>{t("flow.id_control.input.add_nationality_checkbox_yes")}</Base>
              </Checkbox>
              <Checkbox className="ml-4" value={!askToAdd}>
                <Base>{t("flow.id_control.input.add_nationality_checkbox_no")}</Base>
              </Checkbox>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
